import { useCallback, useRef, useState } from "react";
import Cropper, { type Area } from "react-easy-crop";
import { uploadOrgLogo, ApiError } from "../api";

/**
 * Logo uploader — Phase C2.
 *
 * Drag-drop / click-to-pick → crop-to-square in-browser → resize to
 * 512×512 → PNG → upload to R2 via the worker. The whole pipeline
 * happens client-side until the final POST; we never send the
 * original full-res file.
 *
 * UX:
 *   1. Drop-zone (or file picker) accepts PNG/JPEG/WebP up to ~10 MB
 *      raw (we'll shrink it before upload).
 *   2. Cropper modal pops with the image and a square mask. User
 *      drags to position + pinches/scrolls to zoom.
 *   3. "Looking good" commits the crop, renders to canvas at 512²,
 *      uploads, returns the new URL.
 *
 * Fun touches:
 *   - Drop-zone gets a subtle olive glow on dragover
 *   - "Cooking it up…" / "Uploading…" / "Looks epic ✨" copy
 *   - Cropper shows a faint guide grid so the rider's eye lands
 *     in the middle of the square
 */

const OUTPUT_SIZE = 512; // px square — plenty for trail detail screens
const MAX_RAW_BYTES = 10 * 1024 * 1024; // 10 MB raw upload cap
const ACCEPTED = "image/png,image/jpeg,image/webp,image/svg+xml";

export function LogoUploader({
  orgId,
  currentLogoUrl,
  onUploaded,
}: {
  orgId: string;
  currentLogoUrl: string | null;
  /** Fires after a successful upload with the new URL. Parent should
   *  update its local copy of organization.logoUrl. */
  onUploaded: (newUrl: string) => void;
}) {
  const [pickedDataUrl, setPickedDataUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);
  const [uploading, setUploading] = useState(false);
  const [phase, setPhase] = useState<"idle" | "cooking" | "uploading" | "done">(
    "idle"
  );
  /** 0–100 byte-level upload progress reported by XHR. Held in
   *  state so the bar redraws each tick. */
  const [uploadPercent, setUploadPercent] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const onCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    setCroppedArea(areaPixels);
  }, []);

  function pick() {
    fileInputRef.current?.click();
  }

  function loadFile(file: File) {
    setError(null);
    if (!file.type.startsWith("image/")) {
      setError("That doesn't look like an image. PNG, JPEG, or WebP please.");
      return;
    }
    if (file.size > MAX_RAW_BYTES) {
      setError("File is over 10 MB. Shrink it down first.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setPickedDataUrl(e.target?.result as string);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);
  }

  function onFileInput(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) loadFile(file);
    // Reset so picking the same file twice triggers onChange again.
    e.target.value = "";
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) loadFile(file);
  }

  async function commit() {
    if (!pickedDataUrl || !croppedArea) return;
    setUploading(true);
    setPhase("cooking");
    setUploadPercent(0);
    setError(null);
    try {
      const blob = await renderCroppedBlob(pickedDataUrl, croppedArea, OUTPUT_SIZE);
      setPhase("uploading");
      const r = await uploadOrgLogo(orgId, blob, (pct) => setUploadPercent(pct));
      setPhase("done");
      onUploaded(r.logoUrl);
      // Brief celebration, then close the cropper.
      setTimeout(() => {
        setPickedDataUrl(null);
        setPhase("idle");
        setUploading(false);
        setUploadPercent(0);
      }, 900);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Couldn't upload. Try again?"
      );
      setUploading(false);
      setPhase("idle");
      setUploadPercent(0);
    }
  }

  function cancel() {
    setPickedDataUrl(null);
    setCroppedArea(null);
    setPhase("idle");
    setError(null);
  }

  return (
    <>
      <div
        className={`logo-dropzone ${dragOver ? "drag-over" : ""}`}
        onClick={pick}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            pick();
          }
        }}
      >
        <div className="logo-dropzone-preview">
          {currentLogoUrl ? (
            <img src={currentLogoUrl} alt="Current logo" />
          ) : (
            <span className="logo-dropzone-placeholder">🏔</span>
          )}
        </div>
        <div className="logo-dropzone-copy">
          <strong>
            {currentLogoUrl ? "Swap the logo" : "Drop a logo here"}
          </strong>
          <span className="muted">
            Drag in a PNG/JPEG/WebP, or click to pick from disk. We'll crop
            and resize to 512² for you.
          </span>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED}
          onChange={onFileInput}
          style={{ display: "none" }}
        />
      </div>

      {error && (
        <div className="admin-feedback admin-feedback-err" style={{ marginTop: 6 }}>
          {error}
        </div>
      )}

      {pickedDataUrl && (
        <div
          className="logo-cropper-backdrop"
          onClick={uploading ? undefined : cancel}
        >
          <div
            className="logo-cropper-card"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="logo-cropper-head">
              <div>
                <div className="drawer-eyebrow">Crop the logo</div>
                <h3 style={{ margin: "4px 0 0" }}>Find the centerline</h3>
                <p className="muted" style={{ margin: "4px 0 0", fontSize: 12 }}>
                  Drag to position, scroll or pinch to zoom. We crop to a
                  square the apps will love.
                </p>
              </div>
            </header>

            <div className="logo-cropper-stage">
              <Cropper
                image={pickedDataUrl}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="rect"
                showGrid
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>

            <div className="logo-cropper-zoom">
              <span className="muted" style={{ fontSize: 11 }}>Zoom</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
              />
            </div>

            <footer className="logo-cropper-foot">
              {/* Progress bar takes over the status line when an upload
                  is in flight. Visible during BOTH the cooking
                  (indeterminate, pulsing) and uploading (real %) phases
                  so the user never sees the dialog "freeze." */}
              {phase === "idle" && (
                <span className="muted logo-cropper-status">
                  Ready when you are.
                </span>
              )}
              {(phase === "cooking" || phase === "uploading" || phase === "done") && (
                <div className="logo-progress" aria-live="polite">
                  <div className="logo-progress-line">
                    <span>
                      {phase === "cooking" && "Cooking it up…"}
                      {phase === "uploading" &&
                        `Uploading… ${uploadPercent}%`}
                      {phase === "done" && "Looks epic ✨"}
                    </span>
                    {phase === "uploading" && uploadPercent > 0 && (
                      <span className="muted logo-progress-percent">
                        {uploadPercent}%
                      </span>
                    )}
                  </div>
                  <div
                    className={`logo-progress-bar ${phase === "cooking" ? "indeterminate" : ""}`}
                  >
                    <div
                      className="logo-progress-bar-fill"
                      style={{
                        width:
                          phase === "done"
                            ? "100%"
                            : phase === "uploading"
                              ? `${uploadPercent}%`
                              : "100%",
                      }}
                    />
                  </div>
                </div>
              )}
              <div className="row" style={{ gap: 8 }}>
                <button
                  type="button"
                  className="secondary"
                  onClick={cancel}
                  disabled={uploading}
                >
                  Bail out
                </button>
                <button
                  type="button"
                  onClick={commit}
                  disabled={uploading || !croppedArea}
                >
                  {phase === "done" ? "Done ✨" : "Looking good"}
                </button>
              </div>
            </footer>
          </div>
        </div>
      )}
    </>
  );
}

/** Render the cropped region into a square canvas at outputSize px,
 *  then export as PNG. Uses ImageBitmap via createImageBitmap when
 *  available (sharper resampling); falls back to drawImage. */
async function renderCroppedBlob(
  dataUrl: string,
  pixelCrop: Area,
  outputSize: number
): Promise<Blob> {
  const image = await loadImage(dataUrl);
  const canvas = document.createElement("canvas");
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context unavailable");

  // High-quality smoothing — better when scaling down from a large source.
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outputSize,
    outputSize
  );

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error("Failed to encode the image"));
        resolve(blob);
      },
      "image/png",
      0.95
    );
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Couldn't decode the image"));
    img.src = src;
  });
}
